import type { ReactNode } from 'react';
import type {
  CashReceiptCandidateRemittance,
  CashReceiptDetail,
  CashReceiptWorkItemRemittance,
} from '@erp/rpc';
import { T, useGT } from 'gt-react';
import { Link } from '@tanstack/react-router';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowSquareOut, LinkSimple } from '@phosphor-icons/react';
import { serializeMoney, subtractMoney } from '@erp/shared-utils';
import {
  useArCashReceiptWorkItem,
  useLinkCashReceiptRemittance,
} from '@/api/rpc/hooks/accounts-receivable/useArCashReceipts';
import { useToast } from 'src/context/ToastContext';
import { formatAmount, formatDate } from './cashReceiptFormat';

/** Success/failure toasts for a hand-picked remittance link. */
function useLinkResultToasts() {
  const gt = useGT();
  const { showToast } = useToast();
  return {
    linked(amountMismatch: boolean) {
      showToast({
        title: gt('Remittance linked', {
          context: 'toast title, cash receipt linked to a remittance',
        }),
        message: amountMismatch
          ? gt("Heads up: the remittance's total differs from the receipt.", {
              context:
                'toast message warning that the hand-linked remittance total differs from the bank receipt amount',
            })
          : '',
        color: amountMismatch ? 'warning' : 'success',
        icon: amountMismatch ? 'warning' : 'check',
      });
    },
    failed(err: unknown) {
      showToast({
        title: gt('Link failed', {
          context: 'toast title, cash receipt link to a remittance failed',
        }),
        message: err instanceof Error ? err.message : '',
        color: 'error',
        icon: 'warning',
      });
    },
  };
}

type WorkItemLineItem = CashReceiptWorkItemRemittance['lineItems'][number];

/**
 * Invoice amount minus the stated amount — the short-pay (positive) or
 * overpay (negative) the keyer must account for. Null when not computable
 * (deduction lines, unmatched refs, missing amounts).
 */
function invoiceDiff(lineItem: WorkItemLineItem): string | null {
  if (lineItem.kind !== 'invoice_payment') return null;
  if (!lineItem.invoice?.netAmount || !lineItem.statedAmount) return null;
  return serializeMoney(
    subtractMoney(lineItem.invoice.netAmount, lineItem.statedAmount),
  );
}

function MutedDash() {
  return <span className="text-muted-foreground">—</span>;
}

function InvoiceCell({ lineItem }: { lineItem: WorkItemLineItem }) {
  if (!lineItem.invoice) return <MutedDash />;
  return (
    <Link
      to="/accounts-receivable/invoices/$arInvoiceId"
      params={{ arInvoiceId: lineItem.invoice.id }}
      viewTransition
      className="font-medium text-blue-600 hover:underline"
    >
      {lineItem.invoice.invoiceNumber ?? lineItem.rawInvoiceRef}
    </Link>
  );
}

/** Stated amount, sign-flipped for deduction lines. */
function statedAmountDisplay(lineItem: WorkItemLineItem): string {
  return lineItem.kind === 'deduction' && lineItem.statedAmount
    ? formatAmount(`-${lineItem.statedAmount}`)
    : formatAmount(lineItem.statedAmount);
}

function DiffCell({ diff }: { diff: string | null }) {
  if (diff === null) return <MutedDash />;
  if (Number(diff) === 0) {
    return <span className="text-muted-foreground">{formatAmount(diff)}</span>;
  }
  return (
    <span className="font-medium text-amber-600">{formatAmount(diff)}</span>
  );
}

function LineItemRow({ lineItem }: { lineItem: WorkItemLineItem }) {
  return (
    <tr className="border-t">
      <td className="py-1 pr-2">
        <InvoiceCell lineItem={lineItem} />
      </td>
      <td className="py-1 pr-2">{lineItem.rawInvoiceRef}</td>
      <td className="py-1 pr-2 max-w-32 truncate">
        {lineItem.invoice?.customerName ?? <MutedDash />}
      </td>
      <td className="py-1 pr-2 text-right font-medium">
        {statedAmountDisplay(lineItem)}
      </td>
      <td className="py-1 pr-2 text-right">
        {lineItem.invoice?.balanceDue != null ? (
          formatAmount(lineItem.invoice.balanceDue)
        ) : (
          <MutedDash />
        )}
      </td>
      <td className="py-1 text-right">
        <DiffCell diff={invoiceDiff(lineItem)} />
      </td>
    </tr>
  );
}

function RemittanceCard({
  remittance,
  action,
  subtitle,
}: {
  remittance: CashReceiptWorkItemRemittance;
  action?: ReactNode;
  subtitle?: string;
}) {
  const gt = useGT();
  return (
    <div className="rounded-md border bg-muted/30 p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">
          {remittance.statedReferenceNo ??
            remittance.senderEmail ??
            gt('(No reference)', {
              context:
                'AR cash receipt remittance card fallback when the advice has no reference',
            })}
        </span>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-xs">
            {remittance.channel}
          </Badge>
          <span className="text-xs text-muted-foreground">
            {formatDate(remittance.receivedAt)}
          </span>
          <Link
            to="/accounts-receivable/remittances/$remittanceId"
            params={{ remittanceId: remittance.id }}
            viewTransition
            className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline"
          >
            <ArrowSquareOut className="h-3.5 w-3.5" />
            <T context="link, open the remittance advice detail page from the cash receipt">
              Open
            </T>
          </Link>
          {action}
        </div>
      </div>
      {subtitle && (
        <p className="text-xs text-muted-foreground truncate">{subtitle}</p>
      )}
      {remittance.lineItems.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-muted-foreground">
                <th className="py-1 pr-2 font-medium">
                  <T context="AR cash receipt remittance line items column header">
                    Invoice
                  </T>
                </th>
                <th className="py-1 pr-2 font-medium">
                  <T context="AR cash receipt remittance line items column header, invoice reference as stated on the remittance">
                    Stated Ref
                  </T>
                </th>
                <th className="py-1 pr-2 font-medium">
                  <T context="AR cash receipt remittance line items column header">
                    Customer
                  </T>
                </th>
                <th className="py-1 pr-2 font-medium text-right">
                  <T context="AR cash receipt remittance line items column header">
                    Stated Amt
                  </T>
                </th>
                <th className="py-1 pr-2 font-medium text-right">
                  <T context="AR cash receipt remittance line items column header, current open balance of the invoice">
                    Balance
                  </T>
                </th>
                <th className="py-1 font-medium text-right">
                  <T context="AR cash receipt remittance line items column header, invoice amount minus stated amount">
                    Diff
                  </T>
                </th>
              </tr>
            </thead>
            <tbody>
              {remittance.lineItems.map((lineItem) => (
                <LineItemRow key={lineItem.id} lineItem={lineItem} />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          <T context="AR cash receipt remittance card with no line items">
            No line items extracted.
          </T>
        </p>
      )}
    </div>
  );
}

/**
 * A remittance advice that matches the receipt amount and is not linked to a
 * receipt yet: exactly the claim the operator is looking for.
 */
function CandidateRemittanceRow({
  receiptId,
  candidate,
}: {
  receiptId: string;
  candidate: CashReceiptCandidateRemittance;
}) {
  const toasts = useLinkResultToasts();
  const linkMutation = useLinkCashReceiptRemittance();

  async function handleLink() {
    try {
      const { amountMismatch } = await linkMutation.mutateAsync({
        id: receiptId,
        remittanceAdviceId: candidate.id,
      });
      toasts.linked(amountMismatch);
    } catch (err) {
      toasts.failed(err);
    }
  }

  const subtitle =
    [
      candidate.statedCustomerName,
      formatAmount(candidate.statedTotalAmount),
      formatDate(candidate.statedPaymentDate),
    ]
      .filter(Boolean)
      .join(' · ') || undefined;

  return (
    <RemittanceCard
      remittance={candidate}
      subtitle={subtitle}
      action={
        <Button
          variant="outline"
          size="sm"
          className="h-7 shrink-0 text-xs"
          disabled={linkMutation.isPending}
          onClick={() => void handleLink()}
        >
          <LinkSimple className="mr-1 h-4 w-4" />
          <T context="button, link this candidate remittance advice to the cash receipt">
            Link
          </T>
        </Button>
      }
    />
  );
}

interface CashReceiptWorkSectionProps {
  receipt: CashReceiptDetail;
}

/**
 * The keying work attached to a receipt: its linked remittance advices with
 * their claimed line items (what gets keyed into the ledger of record), and —
 * while the receipt is not yet explained — the candidate remittances with
 * one-click hand-pick linking. The receipt's logging task has its own section
 * (`CashReceiptTaskSection`).
 */
export function CashReceiptWorkSection({
  receipt,
}: CashReceiptWorkSectionProps) {
  const { workItem, isLoading, isError, refetch } = useArCashReceiptWorkItem(
    receipt.id,
  );

  // A failed load must not render as an empty section — the candidate rows
  // here are the only hand-pick linking controls, and the parent offers no
  // other error or retry surface.
  if (isError) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-destructive">
          <T context="AR cash receipt work section, failed to load remittances and match candidates">
            Failed to load remittances and match candidates.
          </T>
        </p>
        <Button variant="outline" size="sm" onClick={() => void refetch()}>
          <T context="AR cash receipt work section, retry loading button">
            Retry
          </T>
        </Button>
      </div>
    );
  }

  if (isLoading || !workItem) return null;

  const { remittances, candidateRemittances } = workItem;
  const showRemittances = remittances.length > 0;
  // The server decides whether candidates are still worth hunting for — it
  // returns none once the receipt is genuinely settled — so the list speaks
  // for itself and this doesn't re-derive the rule.
  const showCandidateRemittances = candidateRemittances.length > 0;

  return (
    <div className="space-y-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <T context="AR cash receipt, section heading for remittances and match candidates">
          Remittance
        </T>
      </p>

      {showRemittances && (
        <div className="space-y-2">
          {remittances.map((remittance) => (
            <RemittanceCard key={remittance.id} remittance={remittance} />
          ))}
        </div>
      )}

      {showCandidateRemittances && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            <T context="AR cash receipt, explanation above remittance advices that match the receipt amount and are not linked to a receipt yet">
              Remittances matching this receipt's amount that aren't linked to a
              receipt yet. Link one to match by hand.
            </T>
          </p>
          {candidateRemittances.map((candidate) => (
            <CandidateRemittanceRow
              key={candidate.id}
              receiptId={receipt.id}
              candidate={candidate}
            />
          ))}
        </div>
      )}

      {!showRemittances && !showCandidateRemittances && (
        <div className="rounded-md border border-dashed p-3">
          <p className="text-xs text-muted-foreground">
            <T context="AR cash receipt, empty state when no remittance is linked and none matches the receipt">
              No remittance is linked, and none matches this receipt's exact
              amount within the matching window. If the remittance has arrived,
              check its stated total on the Remittances page — a match will
              appear here as a candidate.
            </T>
          </p>
        </div>
      )}
    </div>
  );
}
